import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  createTheme
} from "react";
import { doc, setDoc, getDoc, collection, addDoc } from "firebase/firestore";
import { db, storage } from "../firebase/config";
import { amber } from "@mui/material/colors";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { Helmet } from "react-helmet-async";

const EventDataContext = createContext(null);

export function useEventData() {
  const context = useContext(EventDataContext);
  if (!context) {
    throw new Error("useEventData must be used within EventDataProvider");
  }
  return context;
}

// Initial data structure for the entire event
const initialEventData = {
  // Information page data
  information: {
    eventName: { value: "", required: true },
    eventDate: { value: null, required: true },
    eventType: { value: "", required: true },
    eventStatus: { value: "", required: true },
    description: { value: "", required: true },
  },

  // Theme page data
  theme: {
    mainColor: { value: "#7B3F00", required: false },
    secondaryColor: { value: "#4B5B63", required: false },
    backgroundColor: { value: "#A34BFF", required: false },
    fontColor: { value: "#4B5B63", required: false },
    backgroundImage: { value: null, required: false },
    selectedFont: { value: "maaja", required: false },
    headerFontSize: { value: 32, required: false },
    detailFontSize: { value: 16, required: false },
    buttonFontSize: { value: 18, required: false },
    borderStyle: { value: "fancy", required: false },
  },

  // Main page data
  main: {
    backgroundImage: { value: null, required: false },
    eventTitle: { value: "", required: false },
    eventDescription: { value: "", required: false },
    textPosition: { value: "center", required: false },
    descriptionAlignment: { value: "center", required: false },
    wishButton: {
      enabled: { value: false, required: false },
      icon: { value: "star", required: false },
      text: { value: "", required: false },
    },
  },

  // Gift page data
  gift: {
    backgroundImage: { value: null, required: false },
    giftButton: {
      enabled: { value: false, required: false },
      icon: { value: "star", required: false },
      text: { value: "", required: false },
    },
    giftReceiverSetup: {
      groomBrideToggle: { value: true, required: false },
    },
    formSettings: {
      nameField: {
        enabled: { value: true, required: false },
        placeholder: { value: "Tell us your name!", required: false },
      },
      uploadSlip: { value: true, required: false },
      allowAnonymous: {
        enabled: { value: true, required: false },
        text: { value: "Anonymous", required: false },
      },
    },
    confirmButton: {
      enabled: { value: true, required: false },
      icon: { value: "star", required: false },
      text: { value: "Confirm", required: false },
    },
    accounts: {
      accountName: {
        value: "",
        required: false,
      },
      accountNumber: {
        value: "",
        required: false,
      },
      accountBank: {
        value: "",
        required: false,
      },
    },
  },

  // Wish page data
  wish: {
    backgroundImage: { value: null, required: false },
    senderNameField: {
      enabled: { value: true, required: false },
      placeholder: { value: "Tell us your name!", required: false },
      characterLimit: { value: 50, required: false },
    },
    wishField: {
      enabled: { value: true, required: false },
      placeholder: { value: "Write your wish...", required: false },
      characterLimit: { value: 200, required: false },
    },
    confirmButton: {
      enabled: { value: true, required: false },
      icon: { value: "star", required: false },
      text: { value: "Confirm", required: false },
    },
    randomWish: {
      enabled: { value: false, required: false },
      texts: { value: [], required: false },
    },
    previewButton: {
      enabled: { value: false, required: false },
      icon: { value: "star", required: false },
      text: { value: "Preview", required: false },
    },
  },

  // Album page data
  album: {
    backgroundImage: { value: null, required: false },
    layout: {
      sortingType: { value: "time", required: false },
      pictureFrameSize: { value: "S", required: false },
      numberOfColumns: { value: "1", required: false },
    },
    likeVote: {
      enabled: { value: true, required: false },
      showNumberOfLikes: { value: true, required: false },
    },
    wishTextDisplay: {
      showWisherName: { value: true, required: false },
      showViewWishes: { value: true, required: false },
    },
    filterOptions: {
      enabled: { value: false, required: false },
      showAll: { value: true, required: false },
      showPhotosOnly: { value: false, required: false },
      showWishesOnly: { value: false, required: false },
    },
  },
};

export function EventDataProvider({ children, eventId: initialEventId }) {
  const [eventData, setEventData] = useState(initialEventData);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [currentEventId, setCurrentEventId] = useState(initialEventId || null);
  const [isLoading, setIsLoading] = useState(!!initialEventId);
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreviewPath = location?.pathname?.includes("/preview");

  React.useEffect(() => {
    if (paramId && paramId !== currentEventId) {
      setCurrentEventId(paramId);
      console.log("Event ID from URL:", paramId);
    }
  }, [paramId, currentEventId]);

  const validateRequiredFields = useCallback(() => {
    const errors = {};

    const checkSection = (section, sectionName) => {
      Object.entries(section).forEach(([key, field]) => {
        if (field && typeof field === "object") {
          if (
            field.hasOwnProperty("value") &&
            field.hasOwnProperty("required")
          ) {
            if (field.required && (!field.value || field.value === "")) {
              if (!errors[sectionName]) errors[sectionName] = {};
              errors[sectionName][key] = "This field is required";
            }
          } else {
            // Handle nested objects
            const nestedErrors = {};
            Object.entries(field).forEach(([nestedKey, nestedField]) => {
              if (
                nestedField &&
                typeof nestedField === "object" &&
                nestedField.hasOwnProperty("value") &&
                nestedField.hasOwnProperty("required")
              ) {
                if (
                  nestedField.required &&
                  (!nestedField.value || nestedField.value === "")
                ) {
                  nestedErrors[nestedKey] = "This field is required";
                }
              }
            });
            if (Object.keys(nestedErrors).length > 0) {
              if (!errors[sectionName]) errors[sectionName] = {};
              errors[sectionName][key] = nestedErrors;
            }
          }
        }
      });
    };

    Object.entries(eventData).forEach(([sectionName, section]) => {
      checkSection(section, sectionName);
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [eventData]);

  const updateEventData = useCallback((section, data) => {
    setEventData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...data,
      },
    }));
    setIsDirty(true);
  }, []);

  const updateNestedEventData = useCallback(
    (section, subsection, data) => {
      setEventData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...prev[section][subsection],
            ...data,
          },
        },
      }));

      validateRequiredFields();

      setIsDirty(true);
    },
    [validateRequiredFields]
  );

  // Clear validation errors when required fields are filled
  useEffect(() => {
    if (Object.keys(validationErrors).length === 0) return;

    const newErrors = { ...validationErrors };
    let hasChanges = false;

    const clearFieldErrors = (section, sectionName) => {
      Object.entries(section).forEach(([key, field]) => {
        if (field && typeof field === "object") {
          if (
            field.hasOwnProperty("value") &&
            field.hasOwnProperty("required")
          ) {
            if (
              field.required &&
              field.value &&
              field.value !== "" &&
              newErrors[sectionName]?.[key]
            ) {
              delete newErrors[sectionName][key];
              hasChanges = true;
            }
          } else {
            // Handle nested objects
            Object.entries(field).forEach(([nestedKey, nestedField]) => {
              if (
                nestedField &&
                typeof nestedField === "object" &&
                nestedField.hasOwnProperty("value") &&
                nestedField.hasOwnProperty("required")
              ) {
                if (
                  nestedField.required &&
                  nestedField.value &&
                  nestedField.value !== "" &&
                  newErrors[sectionName]?.[key]?.[nestedKey]
                ) {
                  delete newErrors[sectionName][key][nestedKey];
                  hasChanges = true;
                  // Clean up empty nested error objects
                  if (Object.keys(newErrors[sectionName][key]).length === 0) {
                    delete newErrors[sectionName][key];
                  }
                }
              }
            });
          }
        }
      });
      // Clean up empty section error objects
      if (
        newErrors[sectionName] &&
        Object.keys(newErrors[sectionName]).length === 0
      ) {
        delete newErrors[sectionName];
        hasChanges = true;
      }
    };

    Object.entries(eventData).forEach(([sectionName, section]) => {
      clearFieldErrors(section, sectionName);
    });

    if (hasChanges) {
      setValidationErrors(newErrors);
    }
  }, [eventData, validationErrors]);

  const saveEventData = useCallback(
    async (eventId = null) => {
      if (isPreviewPath) {
        return {
          success: false,
          message: "Saving is disabled while in preview mode.",
        };
      }
      try {
        // Validate required fields before saving
        const isValid = validateRequiredFields();
        if (!isValid) {
          return {
            success: false,
            message: "Please fill in all required fields before saving.",
            validationErrors,
          };
        }

        console.log("Saving event data:", eventData);

        // Prepare data for Firestore (recursively remove the structure with value/required)
        const firestoreData = {};
        Object.entries(eventData).forEach(([sectionKey, sectionVal]) => {
          firestoreData[sectionKey] = buildFirestoreData(sectionVal);
        });

        // Add metadata
        firestoreData.createdAt = new Date();
        firestoreData.updatedAt = new Date();

        let docId;
        if (eventId) {
          // Update existing event
          docId = eventId;
          await setDoc(doc(db, "events", eventId), firestoreData, {
            merge: true,
          });
        } else {
          // Create new event
          const docRef = await addDoc(collection(db, "events"), firestoreData);
          docId = docRef.id;
        }

        setIsDirty(false);
        return {
          success: true,
          message: eventId
            ? "Event updated successfully!"
            : "Event created successfully!",
          eventId: docId,
        };
      } catch (error) {
        console.error("Failed to save event data:", error);
        return {
          success: false,
          message: `Failed to save event data: ${error.message}`,
        };
      }
    },
    [eventData, validateRequiredFields, validationErrors, isPreviewPath]
  );

  const loadEventData = useCallback(async (eventId) => {
    try {
      console.log("Loading event data for ID:", eventId);
      setIsLoading(true);

      const docRef = doc(db, "events", eventId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const firestoreData = docSnap.data();

        // Convert Firestore data back to our structure format (recursive)
        const loadedEventData = {};
        Object.entries(initialEventData).forEach(([sectionKey, sectionTemplate]) => {
          loadedEventData[sectionKey] = loadSection(sectionTemplate, firestoreData[sectionKey]);
        });

        setEventData(loadedEventData);
        setIsDirty(false);
        console.log("Event data loaded successfully:", loadedEventData);
        return { success: true };
      } else {
        console.warn("Event not found for ID:", eventId);
        return { success: false, message: "Event not found" };
      }
    } catch (error) {
      console.error("Failed to load event data:", error);
      return {
        success: false,
        message: `Failed to load event data: ${error.message}`,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data automatically when eventId changes
  useEffect(() => {
    if (currentEventId) {
      console.log("Auto-loading event data for ID:", currentEventId);
      loadEventData(currentEventId).catch((error) => {
        console.error("Auto-load failed:", error);
      });
    }
  }, [currentEventId, loadEventData]);



  const setEventId = useCallback((eventId) => {
    setCurrentEventId(eventId);
  }, []);

  const gotoWish = () => {
    navigate(`/${currentEventId}/wish`);
  };

  const gotoGift = () => {
    navigate(`/${currentEventId}/gift`);
  };

  const gotoHome = () => {
    navigate(`/${currentEventId}${isPreviewPath ? "/preview" : ""}`);
  };

  const gotoAlbum = () => {
    navigate(`/${currentEventId}/album`);
  };

  // preview navigation
  const gotoPreviewDashboard = () => {
    navigate(`/${currentEventId}/preview`);
  };

  const gotoPreviewAlbum = () => {
    navigate(`/${currentEventId}/preview/album`);
  };

  const gotoPreviewWish = () => {
    navigate(`/${currentEventId}/preview/wish`);
  };

  const gotoPreviewWishLanding = () => {
    navigate(`/${currentEventId}/preview/wish-landing`);
  };

  const gotoPreviewGiftLanding = () => {
    navigate(`/${currentEventId}/preview/gift-landing`);
  };

  const gotoPreviewGift = () => {
    navigate(`/${currentEventId}/preview/gift`);
  };

  const uploadImageToStorage = useCallback(async (file, { folder = 'events', eventId = currentEventId, onProgress } = {}) => {
    if (!file) throw new Error('No file provided');
    if (!eventId) throw new Error('No eventId specified');
    try {
      const path = `${folder}/${eventId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);

      const url = await new Promise((resolve, reject) => {
        uploadTask.on('state_changed', (snapshot) => {
          if (onProgress) {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            onProgress(progress, snapshot);
          }
        }, reject, async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (e) { reject(e); }
        });
      });
      return { success: true, url, path };
    } catch (error) {
      console.error('Image upload failed', error);
      return { success: false, message: error.message };
    }
  }, [currentEventId]);


  const value = {
    eventData,
    updateEventData,
    updateNestedEventData,
    saveEventData,
    loadEventData,
    setEventId,
    currentEventId,
    isLoading,
    isDirty,
    validateRequiredFields,
    validationErrors,
    eventId: currentEventId || initialEventId,
    gotoWish,
    gotoGift,
    gotoHome,
    gotoAlbum,
    // expose preview helpers
    gotoPreviewDashboard,
    gotoPreviewAlbum,
    gotoPreviewWish,
    gotoPreviewWishLanding,
    gotoPreviewGiftLanding,
    gotoPreviewGift,
    uploadImageToStorage
  };

  return (
    <EventDataContext.Provider value={value}>
      <Helmet>
        <title>{eventData?.information?.eventName?.value || "SmileBurst"}</title>
        <link
          rel="icon"
          href={
            eventData?.main?.backgroundImage?.value ||
            eventData?.theme?.backgroundImage?.value ||
            "%PUBLIC_URL%/favicon.ico"
          }
        />
        <link
          rel="apple-touch-icon"
          href={
            eventData?.main?.backgroundImage?.value ||
            eventData?.theme?.backgroundImage?.value ||
            "%PUBLIC_URL%/logo192.png"
          }
        />
        <meta
          name="description"
          content={
            eventData?.information?.description?.value ||
            eventData?.main?.eventDescription?.value ||
            "Join us in celebrating our special day!"
          }
        />
        <meta
          property="og:title"
          content={
            eventData?.information?.eventName?.value ||
            eventData?.main?.eventTitle?.value ||
            "SmileBurst - Wedding Event"
          }
        />
        <meta
          property="og:description"
          content={
            eventData?.information?.description?.value ||
            eventData?.main?.eventDescription?.value ||
            "Join us in celebrating our special day!"
          }
        />
        <meta
          property="og:image"
          content={
            eventData?.main?.backgroundImage?.value ||
            eventData?.theme?.backgroundImage?.value ||
            "%PUBLIC_URL%/logo512.png"
          }
        />
        <meta
          property="twitter:title"
          content={
            eventData?.information?.eventName?.value ||
            eventData?.main?.eventTitle?.value ||
            "SmileBurst - Wedding Event"
          }
        />
        <meta
          property="twitter:description"
          content={
            eventData?.information?.description?.value ||
            eventData?.main?.eventDescription?.value ||
            "Join us in celebrating our special day!"
          }
        />
        <meta
          property="twitter:image"
          content={
            eventData?.main?.backgroundImage?.value ||
            eventData?.theme?.backgroundImage?.value ||
            "%PUBLIC_URL%/logo512.png"
          }
        />
      </Helmet>
      {children}
    </EventDataContext.Provider>
  );
}

// Recursive helper to convert internal state structure -> plain values for Firestore
function buildFirestoreData(structure) {
  const result = {};
  Object.entries(structure || {}).forEach(([key, val]) => {
    if (val && typeof val === "object") {
      if (Object.prototype.hasOwnProperty.call(val, "value") && Object.prototype.hasOwnProperty.call(val, "required")) {
        result[key] = val.value;
      } else {
        // nested container
        const nested = buildFirestoreData(val);
        // Only assign if not empty (keep empty object if structure exists)
        result[key] = nested;
      }
    }
  });
  return result;
}

// Recursive helper to merge Firestore plain data back into the internal template structure
function loadSection(templateSection, dataSection) {
  const result = {};
  Object.entries(templateSection || {}).forEach(([key, templateField]) => {
    if (templateField && typeof templateField === "object") {
      if (Object.prototype.hasOwnProperty.call(templateField, "value") && Object.prototype.hasOwnProperty.call(templateField, "required")) {
        let fieldValue = dataSection?.[key];
        // Special handling for eventDate timestamp conversions
        if (key === "eventDate") {
          if (fieldValue && typeof fieldValue === "object" && fieldValue.seconds) {
            fieldValue = new Date(fieldValue.seconds * 1000);
          } else if (typeof fieldValue === "number") {
            fieldValue = new Date(fieldValue > 10000000000 ? fieldValue : fieldValue * 1000);
          }
        }
        result[key] = { ...templateField, value: fieldValue ?? templateField.value };
      } else {
        result[key] = loadSection(templateField, dataSection?.[key]);
      }
    }
  });
  return result;
}
